/**
 * Catalog-run families: a catalog job is several ordinary runs that share one UUIDv7 job id —
 * `<job>-sample`, `<job>-full`, `<job>-fix<n>`. The service has no job concept, so the id suffix
 * is the only durable tag the app can read back (same approach as the QC `-qc` suffix).
 */
import type { GenerationRunRead } from '../types/content.types';

import { isQcRunId, uuidV7 } from './qc-steps';

/** The run kinds inside a catalog job. */
export type CatalogRunKind = 'sample' | 'full' | 'fix';

/** Every kind `parseRunId` can tell apart; `other` is a run started outside both flows. */
export type RunFamilyKind = 'qc' | CatalogRunKind | 'other';

/** A run id split into its family: the kind, the job it belongs to, and the fix number. */
export interface ParsedRunId {
  kind: RunFamilyKind;
  /** The shared job id for catalog runs; the run id itself for QC and other runs. */
  jobId: string;
  /** 1-based fix number for a `-fix<n>` run, else null. */
  fixN: number | null;
}

/** `<job>-sample` · `<job>-full` · `<job>-fix<n>` (n ≥ 1, no leading zero). */
const CATALOG_RUN_ID = /^(.+)-(sample|full|fix([1-9]\d*))$/;

/** A fresh catalog job id — a UUIDv7, so the family's run ids sort by creation time. */
export function newCatalogJobId(): string {
  return uuidV7();
}

/**
 * The run id for one run of a catalog job. `n` is the fix number (1-based) and is required for
 * `fix`; it is ignored for `sample` and `full`. Throws on a bad fix number rather than minting
 * an id `parseRunId` could not read back.
 */
export function catalogRunId(jobId: string, kind: CatalogRunKind, n?: number): string {
  if (kind !== 'fix') return `${jobId}-${kind}`;
  if (n === undefined || !Number.isInteger(n) || n < 1) {
    throw new Error(`A fix run needs a whole fix number of 1 or more (got ${String(n)})`);
  }
  return `${jobId}-fix${String(n)}`;
}

/**
 * Which family a run belongs to. QC is checked first so both the `-qc` suffix and the legacy
 * `qc-` prefix still read as QC; anything without a known suffix is `other`.
 */
export function parseRunId(runId: string): ParsedRunId {
  if (isQcRunId(runId)) return { kind: 'qc', jobId: runId, fixN: null };
  const match = CATALOG_RUN_ID.exec(runId);
  const jobId = match?.[1];
  const tail = match?.[2];
  if (jobId === undefined || tail === undefined) return { kind: 'other', jobId: runId, fixN: null };
  if (tail === 'sample' || tail === 'full') return { kind: tail, jobId, fixN: null };
  return { kind: 'fix', jobId, fixN: Number(match?.[3]) };
}

/** One catalog job: its optional sample, its full run, its fixes, and when it last moved. */
export interface CatalogJob {
  jobId: string;
  sample?: GenerationRunRead;
  full?: GenerationRunRead;
  /** Ascending by fix number. */
  fixes: GenerationRunRead[];
  /** The newest timestamp across the job's runs (ISO), or null when none carries one. */
  latestActivityAt: string | null;
}

/** `GET /runs` split by family: QC checks, catalog jobs, and runs from neither flow. */
export interface GroupedRuns {
  /** In the order given (the runs list is newest first). */
  qcChecks: GenerationRunRead[];
  /** Most recent activity first. */
  catalogJobs: CatalogJob[];
  others: GenerationRunRead[];
}

/** A run's latest timestamp: updated, else finished, started or created. */
function runActivityAt(run: GenerationRunRead): string | null {
  return run.updatedAt ?? run.finishedAt ?? run.startedAt ?? run.createdAt;
}

/** The later of two ISO instants; null only when both are. */
function laterOf(a: string | null, b: string | null): string | null {
  if (a === null) return b;
  if (b === null) return a;
  return Date.parse(b) > Date.parse(a) ? b : a;
}

/** Keeps the run that moved last when the same slot appears twice (a re-posted id). */
function newerRun(current: GenerationRunRead | undefined, next: GenerationRunRead) {
  if (!current) return next;
  return laterOf(runActivityAt(current), runActivityAt(next)) === runActivityAt(next)
    ? next
    : current;
}

/**
 * Groups loaded runs into QC checks and catalog jobs, client-side — `GET /runs` cannot filter by
 * kind, so this only sees the pages already loaded and a job may be partial until more load.
 */
export function groupRunsIntoJobs(runs: readonly GenerationRunRead[]): GroupedRuns {
  const qcChecks: GenerationRunRead[] = [];
  const others: GenerationRunRead[] = [];
  const jobs = new Map<string, CatalogJob>();
  const fixesByN = new Map<string, Map<number, GenerationRunRead>>();

  for (const run of runs) {
    const parsed = parseRunId(run.runId);
    if (parsed.kind === 'qc') {
      qcChecks.push(run);
      continue;
    }
    if (parsed.kind === 'other') {
      others.push(run);
      continue;
    }
    const job = jobs.get(parsed.jobId) ?? {
      jobId: parsed.jobId,
      fixes: [],
      latestActivityAt: null,
    };
    jobs.set(parsed.jobId, job);
    job.latestActivityAt = laterOf(job.latestActivityAt, runActivityAt(run));
    if (parsed.kind === 'sample') job.sample = newerRun(job.sample, run);
    else if (parsed.kind === 'full') job.full = newerRun(job.full, run);
    else if (parsed.fixN !== null) {
      const fixes = fixesByN.get(parsed.jobId) ?? new Map<number, GenerationRunRead>();
      fixesByN.set(parsed.jobId, fixes);
      fixes.set(parsed.fixN, newerRun(fixes.get(parsed.fixN), run));
    }
  }

  for (const [jobId, fixes] of fixesByN) {
    const job = jobs.get(jobId);
    if (job) job.fixes = [...fixes.entries()].sort(([a], [b]) => a - b).map(([, run]) => run);
  }

  const catalogJobs = [...jobs.values()].sort(
    (a, b) =>
      (b.latestActivityAt ? Date.parse(b.latestActivityAt) : 0) -
      (a.latestActivityAt ? Date.parse(a.latestActivityAt) : 0),
  );
  return { qcChecks, catalogJobs, others };
}

/** The next free fix number in a job (1 when it has none), for `catalogRunId(job, 'fix', n)`. */
export function nextFixNumber(job: Pick<CatalogJob, 'fixes'>): number {
  let max = 0;
  for (const run of job.fixes) max = Math.max(max, parseRunId(run.runId).fixN ?? 0);
  return max + 1;
}
